import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import type { FlowControlPerformMode, SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import { isSimulationShowInfoMediaRow } from '@/helpers/show-info-advance.helper';
import {
  findSimulationRowIndex,
  isSimulationScopeIdleShowing,
  resolveAdjacentSimulationPointer,
  resolveScopeIdlePlayItemId,
  type SimulationCuePlaylistContext,
} from '@/helpers/simulation-cue-engine.helper';

/** Resolve the cue-sheet pointer for the item currently on air / simulated. */
export function resolveCurrentCueSheetPointer(
  rows: SimulationCueSheetRow[],
  performMode: FlowControlPerformMode,
  simulationPointer: number,
  cueState?: FlowCueStateV1,
  options?: { simulationOverridePointer?: number | null },
): number | null {
  if (rows.length === 0) {
    return null;
  }

  if (performMode === 'simulation') {
    const onAirPointer = options?.simulationOverridePointer ?? simulationPointer;
    if (onAirPointer < 0 || onAirPointer >= rows.length) {
      return null;
    }
    return onAirPointer;
  }

  const currentStep = cueState?.currentStep;
  if (currentStep?.itemId) {
    const context: SimulationCuePlaylistContext = {
      scope: currentStep.scope,
      slotId: cueState?.activeSlotId,
      cueItemId: currentStep.cueItemId,
    };
    const index = findSimulationRowIndex(rows, currentStep.itemId, context, cueState?.pointer);
    if (index >= 0) {
      return index;
    }
  }

  if (cueState != null && cueState.pointer >= 0 && cueState.pointer < rows.length) {
    return cueState.pointer;
  }

  return null;
}

/**
 * Next auto-cue target after the current pointer (includes transitions; skips operator-skipped).
 * During an insert override, Next is the resume/auto row (item we return to), not the row after the insert.
 * When the current media row is `show_info` and not already scope-idle, Next is the eject → fallback
 * composite (same pointer), matching live cueState.nextItemId projection.
 */
export function resolveNextCuePreviewRow(
  rows: SimulationCueSheetRow[],
  currentPointer: number | null,
  options?: {
    cueOverrideActive?: boolean;
    resumePointer?: number | null;
    selectedItemIds?: string[];
    flow?: FlowDto;
    /** Session-consumed return_previous keys — same filter as stepMedia / Next take. */
    playedReturnPreviousKeys?: readonly string[];
  },
): SimulationCueSheetRow | null {
  if (rows.length === 0) {
    return null;
  }

  if (options?.cueOverrideActive && options.resumePointer != null) {
    if (options.resumePointer < 0 || options.resumePointer >= rows.length) {
      return null;
    }
    return rows[options.resumePointer] ?? null;
  }

  if (currentPointer == null) {
    return null;
  }

  const playedKeys = options?.playedReturnPreviousKeys?.length ? new Set(options.playedReturnPreviousKeys) : undefined;

  const current = rows[currentPointer];
  const selectedItemIds = options?.selectedItemIds ?? [];
  const scopeIdleShowing = isSimulationScopeIdleShowing(rows, currentPointer, selectedItemIds);
  if (current && isSimulationShowInfoMediaRow(current, options?.flow) && !scopeIdleShowing) {
    const idleItemId = resolveScopeIdlePlayItemId(rows, currentPointer);
    if (idleItemId) {
      const idleIndex = findSimulationRowIndex(
        rows,
        idleItemId,
        { scope: current.scope, slotId: current.slotId },
        currentPointer,
      );
      if (idleIndex >= 0) {
        return rows[idleIndex] ?? null;
      }
      return {
        itemId: idleItemId,
        scope: current.scope,
        slotId: current.slotId,
        label: idleItemId,
        ordinal: current.ordinal,
        segmentKind: current.segmentKind,
        segmentAnchor: current.segmentAnchor,
        fallbackCompositeItemId: current.fallbackCompositeItemId,
      };
    }
  }

  const idleItemId = resolveScopeIdlePlayItemId(rows, currentPointer);
  let nextPointer = resolveAdjacentSimulationPointer(rows, currentPointer, 1, playedKeys);
  // Info composite may also be a cue-sheet row; once scope-idle is showing it is already on air —
  // skip it so Next is the following playable media/transition.
  if (scopeIdleShowing && idleItemId) {
    while (nextPointer != null && rows[nextPointer]?.itemId === idleItemId) {
      nextPointer = resolveAdjacentSimulationPointer(rows, nextPointer, 1, playedKeys);
    }
  }
  if (nextPointer == null) {
    return null;
  }
  return rows[nextPointer] ?? null;
}

/**
 * Row that becomes Next once the upcoming take commits — used to warm the preview layers ahead of
 * the click so an advance can swap frames without a round trip.
 */
export function resolveLookAheadPrefetchRow(
  rows: SimulationCueSheetRow[],
  nextRow: Pick<SimulationCueSheetRow, 'itemId' | 'scope' | 'slotId' | 'cueItemId'> | null,
  options?: { selectedItemIds?: string[]; flow?: FlowDto; playedReturnPreviousKeys?: readonly string[] },
): SimulationCueSheetRow | null {
  if (!nextRow?.itemId || rows.length === 0) {
    return null;
  }
  const index = findSimulationRowIndex(rows, nextRow.itemId, {
    scope: nextRow.scope,
    slotId: nextRow.slotId,
    cueItemId: 'cueItemId' in nextRow ? nextRow.cueItemId : undefined,
  });
  if (index < 0) {
    return null;
  }
  const row = resolveNextCuePreviewRow(rows, index, {
    selectedItemIds: options?.selectedItemIds,
    flow: options?.flow,
    playedReturnPreviousKeys: options?.playedReturnPreviousKeys,
  });
  if (!row || (row.itemId === nextRow.itemId && row.scope === nextRow.scope && row.slotId === nextRow.slotId)) {
    return null;
  }
  return row;
}

/** Already-resolved LIVE Next target from cueState projection (BE-owned). */
export type LiveNextCueTarget = {
  itemId: string;
  cueItemId?: string;
  scope?: string;
  slotId?: string;
};

/** Sheet row or render stub for a resolved LIVE Next identity. */
export function simulationRowFromLiveNextTarget(
  target: LiveNextCueTarget | null | undefined,
  rows: SimulationCueSheetRow[],
  pointer?: number,
): SimulationCueSheetRow | null {
  if (!target?.itemId) {
    return null;
  }
  const index = findSimulationRowIndex(
    rows,
    target.itemId,
    { scope: target.scope, slotId: target.slotId, cueItemId: target.cueItemId },
    pointer,
  );
  if (index >= 0) {
    return rows[index] ?? null;
  }
  return {
    itemId: target.itemId,
    cueItemId: target.cueItemId,
    scope: target.scope,
    slotId: target.slotId,
    label: target.itemId,
    ordinal: 0,
    segmentKind: 'pre_flow',
    segmentAnchor: 'flow_start',
  };
}

/** Slot id encoded as `slot:<id>` on a cue step anchor. */
function slotIdFromSegmentAnchor(anchor: string | undefined): string | undefined {
  if (!anchor?.startsWith('slot:')) {
    return undefined;
  }
  return anchor.slice('slot:'.length);
}

/** Read projected Next identity from cueState — no FE adjacency walk. */
export function resolveLiveNextCueTarget(
  cueState?: FlowCueStateV1,
  insertResume?: { itemId?: string; scope?: string; slotId?: string } | null,
): LiveNextCueTarget | null {
  // Play once: sequential `_lookahead:*` stays the successor of the auto row. Next is the resume
  // overlay (the item we left). Do not wait for cueOverrideActive — skipped GET can drop it.
  if (insertResume?.itemId) {
    return {
      itemId: insertResume.itemId,
      scope: insertResume.scope,
      slotId: insertResume.slotId,
    };
  }
  if (cueState?.cueOverrideActive && cueState.resumeStep?.itemId) {
    return {
      itemId: cueState.resumeStep.itemId,
      scope: cueState.resumeStep.scope,
      slotId: slotIdFromSegmentAnchor(cueState.resumeStep.segmentAnchor),
    };
  }
  const itemId = cueState?.nextItemId;
  if (!itemId) {
    return null;
  }
  return {
    itemId,
    cueItemId: cueState.nextCueItemId,
    scope: cueState.nextScope,
    slotId: cueState.nextSlotId,
  };
}

/** Whether a media-list row is the same cue target shown in the Next preview. */
export function isNextCuePreviewTarget(
  nextRow:
    | Pick<SimulationCueSheetRow, 'itemId' | 'scope' | 'slotId' | 'cueItemId'>
    | LiveNextCueTarget
    | null
    | undefined,
  itemId: string,
  playlistContext?: SimulationCuePlaylistContext,
): boolean {
  if (!nextRow || nextRow.itemId !== itemId) {
    return false;
  }
  const nextCueItemId = nextRow.cueItemId;
  if (playlistContext?.cueItemId && nextCueItemId) {
    return playlistContext.cueItemId === nextCueItemId;
  }
  if (playlistContext?.scope && nextRow.scope && playlistContext.scope !== nextRow.scope) {
    return false;
  }
  if (playlistContext?.slotId && nextRow.slotId && playlistContext.slotId !== nextRow.slotId) {
    return false;
  }
  return true;
}

/** Whether prev/next cue transport can move from the current pointer. */
export function resolveCueTransportCanStep(
  rows: SimulationCueSheetRow[],
  currentPointer: number | null,
  playedReturnPreviousKeys?: readonly string[],
): { canPrev: boolean; canNext: boolean } {
  if (currentPointer == null) {
    return { canPrev: false, canNext: false };
  }
  const playedKeys = playedReturnPreviousKeys?.length ? new Set(playedReturnPreviousKeys) : undefined;
  return {
    canPrev: resolveAdjacentSimulationPointer(rows, currentPointer, -1, playedKeys) != null,
    canNext: resolveAdjacentSimulationPointer(rows, currentPointer, 1, playedKeys) != null,
  };
}

/** Live prev/next: cueState pointer + projected Next. Do not walk the simulation sheet. */
export function resolveLiveCueTransportCanStep(cueState?: FlowCueStateV1): { canPrev: boolean; canNext: boolean } {
  if (!cueState) {
    return { canPrev: false, canNext: false };
  }
  if (cueState.cueOverrideActive) {
    return { canPrev: true, canNext: true };
  }
  const pointer = cueState.pointer ?? 0;
  const totalSteps = cueState.totalSteps ?? 0;
  return {
    canPrev: pointer > 0,
    canNext: Boolean(cueState.nextItemId) || (totalSteps > 0 && pointer < totalSteps - 1),
  };
}
